import React, { useEffect, useState, useCallback } from "react";
import APIHome from "routes/APIHome";
import { BrowserRouter, Route, Routes, Navigate } from "react-router-dom";
import i18n from 'translation';
import { LocalizationProvider } from '@mui/x-date-pickers'
import ThemeProvider from 'theme/ThemeProvider';
import CssBaseline from '@mui/material/CssBaseline';

import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import dayjs from 'dayjs';
import 'dayjs/locale/en'; // English
import 'dayjs/locale/es'; // Spanish
import 'dayjs/locale/fr'; // French
import 'dayjs/locale/pt'; // Portuguese

import PrimaryNavigation from 'components/dashboardParts/PrimaryNavigationWrapper';
import LoginPage from 'routes/LoginPage';
import AdminPage from 'routes/admin/AdminPage';
import ProfilePage, { getAvailableHomepagesForUser, Homepages } from 'routes/ProfilePage';
import PasswordReset from 'routes/PasswordResetPage';
import useAuth from 'system/APIAuth';
import AuthRequired from 'system/AuthRequired';
import { SeatTypes, sessionToken, UserPreference } from 'system/InfegyUser';
import { APIError, registerAPIRequestErrorCallback, unregisterAPIRequestErrorCallback } from 'system/APIRequest';
import ResourceEditorPage from 'components/adminResources/ResourceEditorPage';
import OrderFormPage from 'components/adminResources/OrderFormPage';
import InvoicePage from 'components/adminResources/InvoicePage';
import ResourceManagementPage from 'routes/admin/ResourceManagementPage';
import AdminDashboardPage from 'routes/admin/Dashboard';
import Dashboard from 'routes/dashboard/Dashboard';
import DashboardPageContainer from 'routes/dashboard/DashboardPage';
import DashboardPageRoot from 'routes/dashboard/DashboardPageRoot';
import DashboardEmpty from 'routes/dashboard/DashboardEmpty';
import DashboardRoot from 'routes/dashboard/DashboardRoot';
import { SnackbarProvider, closeSnackbar } from 'notistack';

import { Button } from '@mui/material';
import UserRegistrationPage from 'routes/UserRegistrationPage';
import StartPage from 'routes/StartPage';
import QueryMonitorsPage from 'routes/queryMonitors/QueryMonitorsPage';
import QueryManagementPage from 'routes/queryManagement/QueryManagementPage';
import AIAssistantPage from 'routes/AIAssistant';
import TeamsPage from 'routes/teams/TeamsPage';

import { GoogleOAuthProvider } from '@react-oauth/google';
import DashboardTemplateQueryPage from 'routes/dashboard/DashboardTemplateQueryPage';
import PageNotFound from 'routes/PageNotFound';
import TagManagementPage from 'routes/tagManagement/TagManagementPage';
import ApiUsagePage from 'routes/apiUsage/ApiUsagePage';
import QuickSearchHome from 'routes/QuickSearchHome';
import ViewOnlyHome from "routes/ViewOnlyHome";

// Start: Intercom Service
  window.intercomSettings = { api_base: "https://api-iam.intercom.io", app_id: "tr9asm4d" };
  (function(){var w=window;var ic=w.Intercom;if(typeof ic==="function"){ic('reattach_activator');ic('update',w.intercomSettings);}else{var d=document;var i=function(){i.c(arguments);};i.q=[];i.c=function(args){i.q.push(args);};w.Intercom=i;var l=function(){var s=d.createElement('script');s.type='text/javascript';s.async=true;s.src='https://widget.intercom.io/widget/tr9asm4d';var x=d.getElementsByTagName('script')[0];x.parentNode.insertBefore(s,x);};if(document.readyState==='complete'){l();}else if(w.attachEvent){w.attachEvent('onload',l);}else{w.addEventListener('load',l,false);}}})();
// End: Intercom Service

function RootNavigator() {
  const auth = useAuth();

  if (auth.isAuthenticated() && auth?.user) {
    const user = auth?.user;
    const preferredHomepage = user?.getPreference(UserPreference.DEFAULT_HOMEPAGE);
    const availableHomepages = getAvailableHomepagesForUser(user);

    if (preferredHomepage && availableHomepages.includes(preferredHomepage as Homepages)) {
      return <Navigate to={`/${preferredHomepage}`} />;
    } else if (availableHomepages.length > 0) {
      return <Navigate to={`/${availableHomepages[0] || "start"}`} />;
    }
  } else {
    return <Navigate to="/login" />;
  }
}

export default function App() {
  const [loginMessage, setLoginMessage] = useState<string | undefined>(undefined);
  const auth = useAuth();
  const [locale, setLocale] = useState<string | undefined>("en");

  // Set the locale for dayjs based on the user's preferences
  useEffect(() => {
    if (auth.user?.preferences?.locale) {
      const locale = auth.user.preferences.locale.split('-')[0];
      setLocale(locale);
      dayjs.locale(locale);
    }
  }, [auth.user?.preferences?.locale]);

  // Load Intercom chat service if user is logged in
  useEffect(() => {
    if (auth.user === undefined) {
      Intercom('shutdown');
    } else {
      window.intercomSettings = {
        api_base: "https://api-iam.intercom.io",
        app_id: "tr9asm4d",
        name: auth.user.name,
        email: auth.user.email,
        user_hash: auth.user.intercom_user,
        created_at: (new Date(auth.user.created_at)).getTime() / 1000
      }
      Intercom('boot');
    }
;  }, [auth.user]);

  // Register for API Request errors so we can do UI stuff when they occur
  const handleAPIError = useCallback((e: APIError) => {
    // If we have a token (are authenticated) and get a 401 from somewhere besides login or logout, send us to login
    if (e.code === 401 && e.endpoint !== "login" && e.endpoint !== "logout") {
      if(sessionToken() !== undefined) {
        setLoginMessage(i18n.t("api-request.session-expired"));
      } else {
        setLoginMessage(i18n.t("api-request.login-required"));
      }
    }
  }, [auth]);

  useEffect(() => {
    registerAPIRequestErrorCallback(handleAPIError);

    return () => {
      unregisterAPIRequestErrorCallback(handleAPIError);
    };
  }, [handleAPIError]);

  if (auth?.sessionExpired() && loginMessage === undefined) {
    setLoginMessage(i18n.t("api-request.session-expired"));
  }

  // This LocalizationProvider is used by Material UI to localize Date selectors, among other things
  // The dateAdapter chosen uses the dayjs library for localized formatting
  return (
    <GoogleOAuthProvider clientId="388464758304-n12r4454sl13de6g0acb7j1j0g4bpc2f.apps.googleusercontent.com">
    <ThemeProvider>
      <CssBaseline />
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale={locale}>
        <SnackbarProvider
          maxSnack={6}
          autoHideDuration={5000}
          preventDuplicate={true}
          action={(snackbarId) => (
            <Button
              variant="contained"
              onClick={() => closeSnackbar(snackbarId)}>
              Close
            </Button>
          )}>
        <BrowserRouter
          future={{
            v7_relativeSplatPath: true,
            v7_startTransition: true
          }}>
          <Routes>
            <Route path="/login" element={<LoginPage message={loginMessage} onLogin={() => setLoginMessage(undefined)} />} />
            <Route path="/register" element={<UserRegistrationPage />} />
            <Route path="/iforgot" element={<PasswordReset />} />
            <Route path="/" element={<RootNavigator />} />
            <Route path="*" element={<PageNotFound />} />
            <Route element={<PrimaryNavigation />} >
              { /* Anything in this block will be a page within the PrimaryNaviation wrapper
                Which means it will have the standard nav bar up top and footer at the bottom */ }
              <Route path="/dashboard" element={<DashboardRoot />}>
                <Route path=":dashboardId/:queryIds?" element={<Dashboard/>} >
                  <Route path="empty" element={<DashboardEmpty/>} />
                  <Route path="template/:templateQueryIndices" element={<DashboardTemplateQueryPage/>}>
                    <Route path="page" element={<DashboardPageRoot/>} >
                      <Route path=":dashboardPageId" element={<DashboardPageContainer/>} />
                    </Route>
                  </Route>
                  <Route path="page" element={<DashboardPageRoot/>} >
                    <Route path=":dashboardPageId" element={<DashboardPageContainer/>} />
                  </Route>
                </Route>
              </Route>
              <Route element={<AuthRequired />} >
                { /* Anything in this block has PrimaryNavigation and always requires auth */ }
                <Route path="/start" element={<StartPage />} />
                <Route path="/quick-search" element={<QuickSearchHome />} />
                <Route path="/viewer-home" element={<ViewOnlyHome />} />
                <Route path="/api-home" element={<APIHome />} />
                <Route path="me" element={<ProfilePage />} />
                <Route path="query-monitors" element={<QueryMonitorsPage/>} />
                <Route path="query-management" element={<QueryManagementPage/>} />
                <Route path="tag-management" element={<TagManagementPage/>} />
                <Route path="usage" element={<ApiUsagePage/>} />
                <Route path="ai-assistant" element={<Navigate to="/pulse-ai" replace />} />
                <Route path="pulse-ai" element={<AIAssistantPage />} />
                <Route path="teams" element={<TeamsPage />} />
                <Route path="/admin" element={<AdminPage />}>
                  <Route path="dashboard" element={<AdminDashboardPage asAdmin/>} />
                  <Route path="resources/:endpoint/:resourceId" element={<ResourceEditorPage asAdmin />} />
                  <Route path="resources/:endpoint" element={<ResourceManagementPage asAdmin />} />
                  <Route path="resources" element={<ResourceManagementPage asAdmin />} />
                </Route>
                { /* Billing routes */}
                <Route path="/admin/billing/new-customer" element={<OrderFormPage />} />
                <Route path="/admin/billing/change-order/:accountId" element={<OrderFormPage />} />
                <Route path="/admin/billing/order/:orderId" element={<OrderFormPage />} />
                <Route path="/admin/billing/invoice/:invoiceId" element={<InvoicePage />} />
              </Route>
            </Route>
            <Route element={<AuthRequired />} >
              { /* Anything in this block has PrimaryNavigation and always requires auth */ }
              <Route path="/start" element={<StartPage />} />
              <Route path="me" element={<ProfilePage />} />
              <Route path="query-monitors" element={<QueryMonitorsPage/>} />
              <Route path="query-management" element={<QueryManagementPage/>} />
              <Route path="usage" element={<ApiUsagePage/>} />
              <Route path="ai-assistant" element={<AIAssistantPage />} />
              <Route path="teams" element={<TeamsPage />} />
              <Route path="/admin" element={<AdminPage />}>
                <Route path="dashboard" element={<AdminDashboardPage asAdmin/>} />
                <Route path="resources/:endpoint/:resourceId" element={<ResourceEditorPage asAdmin />} />
                <Route path="resources/:endpoint" element={<ResourceManagementPage asAdmin />} />
                <Route path="resources" element={<ResourceManagementPage asAdmin />} />
              </Route>
            </Route>
          </Routes>
        </BrowserRouter>
        </SnackbarProvider>
      </LocalizationProvider>
    </ThemeProvider>
    </GoogleOAuthProvider>
  );
}